import {
  AccessTime,
  Circle,
  Group,
  Person,
  Work as WorkIcon,
} from "@mui/icons-material";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { WorkItemList, WorkItemStateEnum } from "../../../store/services/timelineApiRTK";
import { getTeamColor, getWorkItemColor, getWorkItemTypeName } from "../../../utils/workItemUtils";
import { useWorkPanel } from "../../../hooks/useWorkPanel";

interface ActivityInfoBarProps {
  activity: WorkItemList;
  onAssignClick: () => void;
  onMoveClick: () => void;
}

const getStateChip = (state: number) => {
  switch (state) {
    case WorkItemStateEnum.Active:
      return { label: "Aktiv", color: "#4caf50" };
    case WorkItemStateEnum.Closed:
      return { label: "Avslutad", color: "#9e9e9e" };
    case WorkItemStateEnum.Draft:
      return { label: "Utkast", color: "#ff9800" };
    case WorkItemStateEnum.Solved:
      return { label: "Löst", color: "#2196f3" };
    default:
      return { label: "Okänd", color: "#9e9e9e" };
  }
};

const formatDateShort = (dateString: string) => {
  try {
    return format(new Date(dateString), "d MMM yyyy", { locale: sv });
  } catch {
    return "";
  }
};

const ActivityInfoBar = ({ activity, onAssignClick, onMoveClick }: ActivityInfoBarProps) => {
  const state = getStateChip(activity.State);
  const teamColor = getTeamColor(activity.Team?.Id);
  const typeColor = getWorkItemColor(activity.Type);
  const { openWork } = useWorkPanel();

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: "grey.100",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Chip
        icon={<Circle sx={{ fontSize: "10px !important", color: `${state.color} !important` }} />}
        label={state.label}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          borderColor: state.color,
          color: state.color,
        }}
      />

      <Chip
        label={getWorkItemTypeName(activity.Type)}
        size="small"
        sx={{
          fontSize: "0.75rem",
          bgcolor: `${typeColor}20`,
          color: typeColor,
          borderColor: typeColor,
          borderWidth: 1,
          borderStyle: "solid",
        }}
      />

      <Tooltip title={activity.ScheduledTo ? "Klicka för att ändra datum" : "Klicka för att schemalägga"} arrow>
        <Chip
          icon={<AccessTime sx={{ fontSize: "14px !important" }} />}
          label={activity.ScheduledTo ? formatDateShort(activity.ScheduledTo) : "Ej schemalagd"}
          size="small"
          variant="outlined"
          onClick={onMoveClick}
          sx={{ fontSize: "0.75rem", cursor: "pointer" }}
        />
      </Tooltip>

      <Tooltip title="Klicka för att ändra tilldelning" arrow>
        <Chip
          icon={<Person sx={{ fontSize: "14px !important" }} />}
          label={activity.AssignedTo?.Name || "Ej tilldelad"}
          size="small"
          color="primary"
          variant="outlined"
          onClick={onAssignClick}
          sx={{ fontSize: "0.75rem", cursor: "pointer" }}
        />
      </Tooltip>

      {activity.Team && (
        <Chip
          icon={<Group sx={{ fontSize: "14px !important", color: `${teamColor} !important` }} />}
          label={activity.Team.Name}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      )}

      {activity.WorkId && (
        <Tooltip title={`Öppna ärende #${activity.WorkId}`} arrow>
          <Chip
            icon={<WorkIcon sx={{ fontSize: "14px !important" }} />}
            label={`Ärende #${activity.WorkId}`}
            size="small"
            color="primary"
            variant="outlined"
            onClick={() => openWork(activity.WorkId!)}
            sx={{ fontSize: "0.75rem", cursor: "pointer", ml: "auto" }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default ActivityInfoBar;
